/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  ConsultProposalResponseDTO,
  ProposalDetailsResponseDTO,
  PROPOSTA_SITUACAO,
  TIPO_PESSOA,
  TIPO_RESIDENCIA,
  UpdateProposalInputDTO
} from "@repo/core/services/entities/Proposals";
import { GenderEnum, LimitReassessmentEnum } from "@repo/core/enums/proposals";
import { FidelityProposalFormType } from "@repo/core/stores/proposalForm/types";
import { ProposalTypeByCode } from "@repo/core/constants/proposals";
import { normalizeBirthDateToBackendDate } from "@repo/core/utils/birthDate.utils";

/**
 * Converts ISO date string to dd/MM/yyyy format
 * Returns empty string if date is invalid or "0001-01-01T00:00:00"
 */
export const formatDateFromISO = (dateString?: string | null): string => {
  if (
    !dateString ||
    dateString === "0001-01-01T00:00:00" ||
    dateString.trim() === ""
  ) {
    return "";
  }

  try {
    // Parse ISO string directly to avoid timezone conversion issues
    // Format: YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss
    const dateMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!dateMatch) {
      return "";
    }

    const year = parseInt(dateMatch[1], 10);
    const month = parseInt(dateMatch[2], 10);
    const day = parseInt(dateMatch[3], 10);

    // Validate date values
    if (isNaN(year) || isNaN(month) || isNaN(day)) {
      return "";
    }
    if (month < 1 || month > 12 || day < 1 || day > 31) {
      return "";
    }

    return `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${year}`;
  } catch {
    return "";
  }
};

/**
 * Converts dd/MM/yyyy date string to ISO format
 * Returns empty string if date is invalid
 */
export const convertDateToISO = (dateString?: string | null): string => {
  if (!dateString || dateString.trim() === "") {
    return "";
  }

  try {
    // Parse dd/MM/yyyy format
    const parts = dateString.split("/");
    if (parts.length !== 3) {
      return "";
    }

    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
    const year = parseInt(parts[2], 10);

    const date = new Date(year, month, day);
    if (isNaN(date.getTime())) {
      return "";
    }

    return date.toISOString();
  } catch {
    return "";
  }
};

/**
 * Maps gender string to GenderEnum
 * Returns null if empty or invalid
 */
export const mapGenderFromString = (
  gender?: string | null
): GenderEnum | null => {
  if (!gender || gender.trim() === "") return null;
  if (gender === "M" || gender.toUpperCase() === "M") return GenderEnum.MALE;
  if (gender === "F" || gender.toUpperCase() === "F") return GenderEnum.FEMALE;
  return null;
};

/**
 * Maps ConsultProposalResponseDTO to FidelityProposalFormType
 */
export const mapToFidelityProposalForm = (
  proposal: Partial<ConsultProposalResponseDTO>
): FidelityProposalFormType => {
  return {
    birthday: formatDateFromISO(proposal.datDataNascimento),
    martialStatus: proposal.strEstadoCivil || "",
    profession: proposal.strProfissao || "",
    gender: proposal.strSexo || ""
  };
};

/**
 * Maps ProposalDetailsResponseDTO to ConsultProposalResponseDTO
 * Transforms the new API response format to the expected format used throughout the application
 */
export const mapProposalDetailsToConsultProposal = (
  details: ProposalDetailsResponseDTO
): Partial<ConsultProposalResponseDTO> => {
  // Convert datDataProposta from dd/MM/yyyy to ISO format
  const datDataPropostaISO = convertDateToISO(details.datDataProposta);

  // Only include fields that exist in the consultation response or will be used in forms
  return {
    lngProposta: details.lngProposta,
    strNome: details.strNome || "",
    strCpf: details.strCpf || "",
    strCliente: details.strCliente || "",
    strProduto: details.strProduto || "",
    strProdutoDescricao: details.strProdutoDescricao || "",
    strSituacao: details.strSituacao || "",
    datDataProposta: datDataPropostaISO,
    strTipoProposta: ProposalTypeByCode.FIDELIDADE,
    strPontoAtendimentoDescricao: details.strPontoAtendimentoDescricao || "",
    strTokenValidado: details.strTokenValidado || "",
    strFoneEnvioToken: details.strFoneEnvioToken || "",
    strDDDEnvioToken: details.strDDDEnvioToken || "",
    strReavaliacaoLimite:
      details.strReavaliacaoLimite || LimitReassessmentEnum.ANALYSIS,
    lstDocumentos: details.lstDocumentos || [],
    // Fields that will be filled by forms (initialized as empty)
    strEstadoCivil: "",
    strSexo: "",
    strProfissao: "",
    datDataNascimento: ""
  };
};

/**
 * Removes formatting from CPF/CNPJ (removes dots, dashes, and slashes)
 * Returns only numbers
 */
const removeCpfCnpjFormatting = (value?: string | null): string => {
  if (!value) return "";
  return value.replace(/[./-]/g, "");
};

/**
 * Converts ISO date string to YYYY-MM-DD format
 * Returns undefined if date is invalid
 */
const convertISOToYYYYMMDD = (
  dateString?: string | null
): string | undefined => {
  return normalizeBirthDateToBackendDate(dateString);
};

/**
 * Maps fidelity proposal data from store to UpdateProposalInputDTO for saving
 * Includes fixed fields and field transformations specific to fidelity proposals
 */
export const mapFidelityProposalToSavePayload = (
  proposalData: Partial<ConsultProposalResponseDTO>
): UpdateProposalInputDTO => {
  if (!proposalData.lngProposta) {
    throw new Error("lngProposta is required");
  }

  // Get CPF/CNPJ without formatting
  const cpfOrCnpj = proposalData.strCpf || proposalData.strCliente || "";
  const strClienteClean = removeCpfCnpjFormatting(cpfOrCnpj);

  // Convert date from ISO to YYYY-MM-DD format
  const datNascimento = convertISOToYYYYMMDD(proposalData.datDataNascimento);

  // Get phone fields - use strCelular/strCelularDDD if available, otherwise use strFoneEnvioToken/strDDDEnvioToken
  // These fields are required by the API, so we must include them
  const strEndFone1 =
    proposalData.strCelular || proposalData.strFoneEnvioToken || "";
  const strEndDdd1 =
    proposalData.strCelularDDD || proposalData.strDDDEnvioToken || "";

  // Validate required phone fields
  if (!strEndDdd1 || strEndDdd1.trim() === "") {
    throw new Error(
      "DDD do telefone é obrigatório. Verifique se os dados da proposta foram carregados corretamente."
    );
  }
  if (!strEndFone1 || strEndFone1.trim() === "") {
    throw new Error(
      "Número do telefone é obrigatório. Verifique se os dados da proposta foram carregados corretamente."
    );
  }

  // Helper function to include field only if not empty
  const includeIfNotEmpty = <T>(
    value: T | null | undefined | "",
    key: string
  ): Record<string, T> | Record<string, never> => {
    if (value === null || value === undefined || value === "") {
      return {};
    }
    return { [key]: value };
  };

  // Create base payload - exclude fields that will be transformed to avoid conflicts
  // Also exclude strProfissaoDescricao as it's only for frontend display, not sent to backend
  // Exclude strProfissao and strProfissao1 as they need to be mapped to strAtivProfissao
  const {
    strCelular: _strCelular,
    strCelularDDD: _strCelularDDD,
    strFoneEnvioToken: _strFoneEnvioToken,
    strDDDEnvioToken: _strDDDEnvioToken,
    datDataNascimento: _datDataNascimento,
    strEstadoCivil: _strEstadoCivil,
    strCpf: _strCpf,
    strCliente: _strCliente,
    strProfissao: _strProfissao, // Exclude - will be mapped to strAtivProfissao
    strProfissao1: _strProfissao1, // Exclude - not expected by backend
    strProfissaoDescricao: _strProfissaoDescricao, // Frontend-only field, not sent to backend
    strTipoPessoa: _strTipoPessoa,
    ...restProposalData
  } = proposalData;

  // Create base payload with all fields from store (spread first)
  const payload: UpdateProposalInputDTO = {
    // Spread remaining fields from store first
    ...restProposalData,
    // Override with transformed and fixed fields
    lngProposta: String(proposalData.lngProposta),
    // Fixed fields for fidelity proposals
    strEndTipoFone1: TIPO_RESIDENCIA.RESIDENCIAL,
    strSituacao: proposalData.strSituacao as PROPOSTA_SITUACAO,
    strEnvioFatura: proposalData.strEnvioFatura as
      | "E"
      | "W"
      | "T"
      | "C"
      | undefined,
    strResTipoFone1: TIPO_RESIDENCIA.RESIDENCIAL,
    strTipoPessoa:
      proposalData.strTipoPessoa === TIPO_PESSOA.JURIDICA
        ? TIPO_PESSOA.JURIDICA
        : TIPO_PESSOA.FISICA,
    intDiaCartao: 1,
    strTipoProposta: ProposalTypeByCode.FIDELIDADE,
    strReavaliacaoLimite:
      proposalData.strReavaliacaoLimite || LimitReassessmentEnum.ANALYSIS,
    // Transformed fields (override original field names)
    ...(datNascimento && { datNascimento }),
    ...(proposalData.strEstadoCivil && {
      strEstCivil: proposalData.strEstadoCivil
    }),
    // Map strProfissao to strAtivProfissao for the API (backend expects strAtivProfissao, not strProfissao)
    ...includeIfNotEmpty(proposalData.strProfissao, "strAtivProfissao"),
    // Phone fields - always include (validated above, must be after spread to override)
    strEndFone1,
    strEndDdd1,
    strInfoEmail: proposalData.strEmail,
    strEmail: proposalData.strEmail,
    ...(strClienteClean && { strCliente: strClienteClean })
  };

  return payload;
};
